import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, MapPin, Home, Plus, Pencil, Trash2, Phone, Mail, CalendarClock, TrendingUp, Clock,
  DollarSign, Users, FileText, Calculator, ReceiptText, Briefcase, ClipboardCheck, StickyNote,
  PhoneCall, MessageSquare, Star, ImagePlus, Loader2, FileIcon, Paperclip,
} from "lucide-react";
import { TagChip, TagPicker } from "@/components/TagPicker";
import CustomerFormDialog from "@/components/CustomerFormDialog";
import DeleteCustomerDialog from "@/components/DeleteCustomerDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (p) => `${API}/files/${p}?auth=${localStorage.getItem("nlc_token")}`;
const isImg = (n = "") => /\.(png|jpe?g|gif|webp|heic)$/i.test(n);
const hrs = (m) => `${(m / 60).toFixed(1)}h`;

const noteIcons = { note: StickyNote, call: PhoneCall, text: MessageSquare, email: Mail };
const statusColor = {
  draft: "bg-muted text-muted-foreground", sent: "bg-accent/15 text-accent",
  accepted: "bg-primary/10 text-primary", paid: "bg-primary/10 text-primary",
  declined: "bg-destructive/10 text-destructive", overdue: "bg-destructive/10 text-destructive",
  scheduled: "bg-accent/15 text-accent", in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-primary/10 text-primary", pending_review: "bg-amber-100 text-amber-700",
  approved: "bg-primary/10 text-primary", rejected: "bg-destructive/10 text-destructive",
};

const Stat = ({ icon: Icon, label, value, sub, tone = "" }) => (
  <Card className={`p-4 ${tone}`}>
    <div className="flex items-center justify-between">
      <span className="overline text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 opacity-60" />
    </div>
    <div className="font-display text-2xl font-black mt-1.5">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </Card>
);

const emptyProp = { label: "", address: "", city: "", state: "", zip_code: "", lot_size: "", gate_code: "", is_primary: false, photo: "", notes: "" };
const PRESET_SERVICES = ["Lawn Mowing", "Edging & Trimming", "Fertilization", "Leaf Removal", "Aeration", "Landscaping", "Snow Removal", "Cleanup"];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [propOpen, setPropOpen] = useState(false);
  const [propForm, setPropForm] = useState(emptyProp);
  const [propEditId, setPropEditId] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [propPhotoBusy, setPropPhotoBusy] = useState(false);
  const [tagLib, setTagLib] = useState([]);
  const [svc, setSvc] = useState([]);
  const [svcSaving, setSvcSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const fileRef = useRef(null);
  const tagById = Object.fromEntries(tagLib.map((t) => [t.id, t]));

  const load = () => api.get(`/customers/${id}/detail`).then((r) => { setD(r.data); setSvc(r.data.customer?.default_services || []); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/customer-tags").then((r) => setTagLib(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [id]);

  const toggleTag = async (tagId) => {
    const current = d?.customer?.tags || [];
    const next = current.includes(tagId) ? current.filter((t) => t !== tagId) : [...current, tagId];
    setD((prev) => ({ ...prev, customer: { ...prev.customer, tags: next } }));
    try { await api.put(`/customers/${id}/tags`, { tags: next }); }
    catch (e) { toast.error("Could not update tags"); load(); }
  };

  const svcTotal = svc.reduce((a, s) => a + (Number(s.price) || 0), 0);
  const addSvc = (name) => setSvc((l) => [...l, { id: uid(), name: name || "", price: 0 }]);
  const updSvc = (sid, k, v) => setSvc((l) => l.map((s) => (s.id === sid ? { ...s, [k]: v } : s)));
  const rmSvc = (sid) => setSvc((l) => l.filter((s) => s.id !== sid));
  const saveSvc = async () => {
    setSvcSaving(true);
    const cleaned = svc.filter((s) => (s.name || "").trim() || Number(s.price)).map((s) => ({ id: s.id || uid(), name: s.name, price: Number(s.price) || 0 }));
    const payload = { ...d.customer, default_services: cleaned };
    delete payload.id; delete payload.created_at;
    try {
      await api.put(`/customers/${id}`, payload);
      toast.success("Service pricing saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSvcSaving(false); }
  };

  if (!d) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const c = d.customer;
  const p = d.profitability;

  const savePropPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPropPhotoBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPropForm((f) => ({ ...f, photo: data.path }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPropPhotoBusy(false); }
  };

  const saveProp = async () => {
    try {
      if (propEditId) await api.put(`/customers/${id}/properties/${propEditId}`, propForm);
      else await api.post(`/customers/${id}/properties`, propForm);
      toast.success("Property saved"); setPropOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delProp = async (pid) => { await api.delete(`/customers/${id}/properties/${pid}`); load(); };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/customers/${id}/notes`, { text: noteText, type: noteType });
    setNoteText(""); load();
  };
  const delNote = async (nid) => { await api.delete(`/customers/${id}/notes/${nid}`); load(); };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/customers/${id}/files`, { path: data.path, name: file.name });
      toast.success("File attached"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const delFile = async (fid) => { await api.delete(`/customers/${id}/files/${fid}`); load(); };

  const openNewProp = () => { setPropForm({ ...emptyProp, is_primary: d.properties.length === 0 }); setPropEditId(null); setPropOpen(true); };
  const openEditProp = (pr) => { setPropForm({ ...emptyProp, ...pr }); setPropEditId(pr.id); setPropOpen(true); };

  const Row = ({ items, label, icon: Icon, render }) => (
    <div>
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><Icon className="h-4 w-4 text-primary" />{label} <span className="text-muted-foreground font-normal">({items.length})</span></div>
      {items.length === 0 ? <p className="text-xs text-muted-foreground pl-6 pb-3">None yet.</p> : (
        <div className="space-y-1.5 pl-6 pb-3">{items.map(render)}</div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <button onClick={() => navigate("/customers")} data-testid="back-to-customers" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Customers
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">{c.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
            {c.email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{c.email}</span>}
            {c.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
            <Badge variant="outline" className="capitalize">{(c.status || "active") === "active" ? "Currently being serviced" : "Not being serviced"}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="detail-customer-tags">
            {(c.tags || []).map((tid) => tagById[tid] && (
              <TagChip key={tid} tag={tagById[tid]} onRemove={toggleTag} />
            ))}
            <TagPicker library={tagLib} selected={c.tags || []} onToggle={toggleTag} triggerTestId="detail-tag-picker" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="detail-edit-customer-btn"><Pencil className="h-4 w-4 mr-1.5" />Edit</Button>
          <Button variant="outline" onClick={() => setDelOpen(true)} data-testid="detail-delete-customer-btn" className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>
        </div>
      </div>

      {/* Key insights */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={CalendarClock} label="Next Service" value={d.next_service ? new Date(d.next_service.date + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"} sub={d.next_service?.title || "Nothing scheduled"} />
        <Stat icon={TrendingUp} label="Profit / Customer" value={money(p.profit)} sub={`${p.margin}% margin · ${money(p.job_revenue)} rev`} tone={p.profit >= 0 ? "" : "border-destructive/40"} />
        <Stat icon={Clock} label="Avg Job Time" value={p.avg_job_minutes ? hrs(p.avg_job_minutes) : "—"} sub={`${p.jobs_completed} completed · ${p.total_hours}h total`} />
        <Stat icon={DollarSign} label="Outstanding" value={money(d.billing.outstanding)} sub={`${money(d.billing.paid)} paid of ${money(d.billing.invoiced)}`} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="detail-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-properties">Properties ({d.properties.length})</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes ({d.notes_log.length})</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">Files ({d.files.length})</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5 space-y-3">
              <h3 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Crew that services this lawn</h3>
              {d.crew_stats.length === 0 ? <p className="text-sm text-muted-foreground">No labor logged yet.</p> : (
                <div className="space-y-2">
                  {d.crew_stats.map((cs) => (
                    <div key={cs.user_id} className="flex items-center justify-between text-sm">
                      <span className="font-medium">{cs.name}</span>
                      <span className="text-muted-foreground">{cs.jobs} jobs · {cs.hours}h</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card className="p-5 space-y-2">
              <h3 className="font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />Profitability</h3>
              <div className="text-sm space-y-1.5">
                <div className="flex justify-between"><span className="text-muted-foreground">Revenue (completed jobs)</span><span className="font-semibold tabular-nums">{money(p.job_revenue)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Labor cost</span><span className="font-semibold tabular-nums text-destructive">-{money(p.labor_cost)}</span></div>
                <div className="flex justify-between border-t pt-1.5"><span className="font-semibold">Net profit</span><span className={`font-bold tabular-nums ${p.profit >= 0 ? "text-primary" : "text-destructive"}`}>{money(p.profit)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Avg time on site</span><span className="tabular-nums">{p.avg_job_minutes ? hrs(p.avg_job_minutes) : "—"}</span></div>
              </div>
              <p className="text-xs text-muted-foreground pt-1">Driving time & supplies cost aren't tracked yet — coming in a future update.</p>
            </Card>
          </div>
          <Card className="p-5 space-y-3" data-testid="regular-service-card">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2"><DollarSign className="h-4 w-4 text-primary" />Regular Service & Pricing</h3>
              <Button size="sm" onClick={saveSvc} disabled={svcSaving} data-testid="save-service-pricing" className="bg-primary text-primary-foreground hover:bg-[#152e17]">
                {svcSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">Set the price for this customer's regular lawn service (and any add-ons). These auto-load onto new jobs when you pick this customer while scheduling.</p>
            <div className="space-y-2">
              {svc.map((s) => (
                <div key={s.id} className="flex items-center gap-2" data-testid="service-row">
                  <Input className="flex-1" value={s.name} onChange={(e) => updSvc(s.id, "name", e.target.value)} placeholder="Service name (e.g. Lawn Mowing)" data-testid="service-name-input" />
                  <div className="relative w-28">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input type="number" className="pl-5" value={s.price} onChange={(e) => updSvc(s.id, "price", e.target.value)} data-testid="service-price-input" />
                  </div>
                  <button type="button" onClick={() => rmSvc(s.id)} data-testid="remove-service-btn" className="p-2 rounded hover:bg-destructive/10 transition-colors"><Trash2 className="h-4 w-4 text-destructive" /></button>
                </div>
              ))}
              {svc.length === 0 && <p className="text-sm text-muted-foreground">No services set yet. Add the regular lawn service below.</p>}
            </div>
            <div className="flex items-center gap-2">
              <Select value="" onValueChange={(v) => addSvc(v)}>
                <SelectTrigger className="flex-1" data-testid="add-service-select"><SelectValue placeholder="Add a service…" /></SelectTrigger>
                <SelectContent>{PRESET_SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" onClick={() => addSvc("")} data-testid="add-custom-service-btn"><Plus className="h-4 w-4 mr-1" />Custom</Button>
            </div>
            {svc.length > 0 && (
              <div className="flex justify-between items-center rounded-md bg-muted px-3 py-2 text-sm font-semibold" data-testid="service-total">
                <span>Regular job total</span><span>{money(svcTotal)}</span>
              </div>
            )}
          </Card>
          {c.notes && <Card className="p-5"><h3 className="font-semibold mb-1.5">Customer notes</h3><p className="text-sm text-muted-foreground whitespace-pre-wrap">{c.notes}</p></Card>}
        </TabsContent>
        <TabsContent value="properties" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewProp} data-testid="add-property-btn"><Plus className="h-4 w-4 mr-1.5" />Add Property</Button>
          </div>
          {d.properties.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground border-dashed">No properties yet. Add every address you service for this customer.</Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {d.properties.map((pr) => (
                <Card key={pr.id} data-testid="property-card" className="overflow-hidden">
                  {pr.photo ? <img src={fileUrl(pr.photo)} alt="" className="h-32 w-full object-cover" /> : <div className="h-32 w-full bg-muted flex items-center justify-center"><Home className="h-8 w-8 text-muted-foreground/40" /></div>}
                  <div className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold flex items-center gap-2">{pr.label || "Property"}{pr.is_primary && <Badge className="bg-primary/10 text-primary text-[0.6rem]">Primary</Badge>}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5"><MapPin className="h-3.5 w-3.5" />{[pr.address, pr.city, pr.state].filter(Boolean).join(", ") || "No address"}</div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openEditProp(pr)} data-testid="edit-property-btn" className="p-1.5 rounded hover:bg-muted"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></button>
                        <button onClick={() => delProp(pr.id)} data-testid="delete-property-btn" className="p-1.5 rounded hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {pr.lot_size && <span className="bg-muted rounded px-2 py-0.5">Lot: {pr.lot_size}</span>}
                      {pr.gate_code && <span className="bg-muted rounded px-2 py-0.5">Gate: {pr.gate_code}</span>}
                    </div>
                    {pr.notes && <p className="text-xs text-muted-foreground">{pr.notes}</p>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="activity" className="mt-4">
          <Card className="p-5">
            <Row items={d.jobs} label="Jobs" icon={Briefcase} render={(j) => (
              <div key={j.id} className="flex items-center justify-between text-sm">
                <span>{j.title} <span className="text-muted-foreground">· {j.scheduled_date}</span></span>
                <Badge className={statusColor[j.status] || ""}>{j.status}</Badge>
              </div>
            )} />
            <Row items={d.quotes} label="Quotes" icon={Calculator} render={(x) => (
              <div key={x.id} className="flex items-center justify-between text-sm"><span>{x.quote_number || "Quote"} · {money(x.total)}</span><Badge className={statusColor[x.status] || ""}>{x.status}</Badge></div>
            )} />
            <Row items={d.estimates} label="Estimates" icon={FileText} render={(x) => (
              <div key={x.id} className="flex items-center justify-between text-sm"><span>{x.estimate_number || "Estimate"} · {money(x.total)}</span><Badge className={statusColor[x.status] || ""}>{x.status}</Badge></div>
            )} />
            <Row items={d.invoices} label="Invoices" icon={ReceiptText} render={(x) => (
              <div key={x.id} className="flex items-center justify-between text-sm"><span>{x.invoice_number || "Invoice"} · {money(x.total)}</span><Badge className={statusColor[x.status] || ""}>{x.status}</Badge></div>
            )} />
            <Row items={d.quality_checks} label="Quality Checks" icon={ClipboardCheck} render={(x) => (
              <div key={x.id} className="flex items-center justify-between text-sm"><span>{x.job_title || x.service_type}</span><Badge className={statusColor[x.status] || ""}>{x.status?.replace("_", " ")}</Badge></div>
            )} />
          </Card>
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="mt-4 space-y-4">
          <Card className="p-4 space-y-3">
            <div className="flex gap-2">
              <Select value={noteType} onValueChange={setNoteType}>
                <SelectTrigger className="w-32" data-testid="note-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">Note</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
              <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Log a call, text, or note…" data-testid="note-input" />
              <Button onClick={addNote} data-testid="add-note-btn">Add</Button>
            </div>
          </Card>
          <div className="space-y-2">
            {d.notes_log.length === 0 ? <p className="text-sm text-muted-foreground">No communication logged yet.</p> : d.notes_log.map((n) => {
              const Icon = noteIcons[n.type] || StickyNote;
              return (
                <Card key={n.id} data-testid="note-item" className="p-3 flex items-start gap-3">
                  <Icon className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 capitalize">{n.type} · {n.by} · {new Date(n.at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => delNote(n.id)} className="p-1 rounded hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* FILES */}
        <TabsContent value="files" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => fileRef.current?.click()} data-testid="upload-file-btn"><Paperclip className="h-4 w-4 mr-1.5" />Attach File</Button>
            <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} />
          </div>
          {d.files.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground border-dashed">No files yet. Attach contracts, photos, or documents.</Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {d.files.map((f) => (
                <Card key={f.id} data-testid="file-item" className="p-2 group relative">
                  {isImg(f.name) ? <img src={fileUrl(f.path)} alt={f.name} className="h-28 w-full object-cover rounded" /> : <div className="h-28 w-full bg-muted rounded flex items-center justify-center"><FileIcon className="h-8 w-8 text-muted-foreground/50" /></div>}
                  <div className="flex items-center justify-between mt-1.5">
                    <a href={fileUrl(f.path)} target="_blank" rel="noreferrer" className="text-xs truncate hover:underline flex-1">{f.name || "file"}</a>
                    <button onClick={() => delFile(f.id)} className="p-1 rounded hover:bg-destructive/10"><Trash2 className="h-3 w-3 text-destructive" /></button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Property dialog */}
      <Dialog open={propOpen} onOpenChange={setPropOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{propEditId ? "Edit Property" : "Add Property"}</DialogTitle>
            <DialogDescription>Details for one serviced address.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>Label / Nickname</Label><Input data-testid="prop-label" value={propForm.label} onChange={(e) => setPropForm({ ...propForm, label: e.target.value })} placeholder="e.g. Main House, Rental #2" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input data-testid="prop-address" value={propForm.address} onChange={(e) => setPropForm({ ...propForm, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>City</Label><Input value={propForm.city} onChange={(e) => setPropForm({ ...propForm, city: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>State</Label><Input value={propForm.state} onChange={(e) => setPropForm({ ...propForm, state: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Zip</Label><Input value={propForm.zip_code} onChange={(e) => setPropForm({ ...propForm, zip_code: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Lot Size</Label><Input value={propForm.lot_size} onChange={(e) => setPropForm({ ...propForm, lot_size: e.target.value })} placeholder="0.3 acre" /></div>
            <div className="space-y-1.5"><Label>Gate / Access Code</Label><Input value={propForm.gate_code} onChange={(e) => setPropForm({ ...propForm, gate_code: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={propForm.notes} onChange={(e) => setPropForm({ ...propForm, notes: e.target.value })} placeholder="Dog in backyard, park on street, etc." /></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Property Photo</Label>
              <div className="flex items-center gap-3">
                {propForm.photo && <img src={fileUrl(propForm.photo)} alt="" className="h-14 w-14 rounded object-cover border" />}
                <label className="h-14 w-14 rounded border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-primary text-muted-foreground">
                  {propPhotoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  <input type="file" accept="image/*" className="hidden" onChange={savePropPhoto} />
                </label>
              </div>
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-md border p-3">
              <div><Label>Primary property</Label><p className="text-xs text-muted-foreground">Main address for this customer</p></div>
              <Switch checked={propForm.is_primary} onCheckedChange={(v) => setPropForm({ ...propForm, is_primary: v })} data-testid="prop-primary-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPropOpen(false)}>Cancel</Button>
            <Button onClick={saveProp} data-testid="save-property-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerFormDialog open={editOpen} onOpenChange={setEditOpen} customer={c} onSaved={load} />
      <DeleteCustomerDialog customer={delOpen ? c : null} onOpenChange={setDelOpen} onDeleted={() => navigate("/customers")} />
    </div>
  );
}
